<script setup lang="ts">
import { computed, markRaw, nextTick, onMounted, provide, Ref, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { RRule } from 'rrule';
import Chip from 'primevue/chip';
import InputSwitch from 'primevue/inputswitch';
import MultiSelect from 'primevue/multiselect';
import Dialog from 'primevue/dialog';
import Editor from 'primevue/editor';
import Dropdown from 'primevue/dropdown';
import useVuelidate from '@vuelidate/core';
import type Quill from 'quill';
import { helpers, requiredIf } from '@vuelidate/validators';
import { can, useDateTime, useGetInitialRRuleObject, usePersonTypes } from '@/composables';
import { PercentageGoals, TrialsGoals } from '@/components/Goals';
import { EMIT, PERMISSIONS, GOAL_TYPE, EVENT_TYPE, THERAPY_RESULT_TYPES_NAME } from '@/enum';
import { ClinicianService } from '@/services';
import { useCalendarStore, useEventsModalsStore, useAuthStore } from '@/store';
import {
  Attendee,
  DetailedEventInterface,
  GoalInterface,
  GoalMeasurementsInterface,
  TherapyResultsInterface,
  TherapyResultTypeInterface,
  TreatmentPlanServiceStudentInterface,
} from '@/types';
import DocumentationItem from '@/views/Calendar/components/EventModal/components/DocumentationItem.vue';
import CalendarOverlappingEvents from '@/components/CalendarOverlappingEvents';
import { EventProcessTypeModal } from './index';
import templates from './DocumentEventTemplates/document_events_templates';
import EventEvaluationAssessmentsFiles from './EventEvaluationAssessmentsFiles.vue';

const emit = defineEmits([
  EMIT.SAVE_DOCUMENT_EVENT,
  EMIT.CLOSE_MODAL,
  EMIT.EDIT_EVENT,
  'openPastDocuments',
  'changeMultiAttendee',
]);
const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const authStore = useAuthStore();
const submitted: Ref<boolean> = ref(false);
const dateUtilities = useDateTime();

interface Props {
  eventToDocument: DetailedEventInterface;
  documentModalOpened: boolean;
}

const props = defineProps<Props>();
const event = ref<DetailedEventInterface>(props.eventToDocument);

const { therapyResultTypes } = storeToRefs(calendarStore);
const { clickedEventClickArgs } = storeToRefs(eventsModalsStore);

const displayDocumentVirtualEventConfirmModal = ref(false);
const overlappingError = ref<boolean>(false);
const displayPastEventsModal = ref(false);
const isCaseManagerOwnEvent = usePersonTypes.isCaseManager() && event.value.host_id === authStore.user?.id;
const isAdmin =
  usePersonTypes.isSupportAdminOrSchoolAdmin() ||
  usePersonTypes.isSuperAdmin() ||
  (usePersonTypes.isCaseManager() && !isCaseManagerOwnEvent);
const isClinician = usePersonTypes.isClinician() || isCaseManagerOwnEvent;
const isLocked = calendarStore.selectedEvent.is_locked;
const isEditable = dateUtilities.isSameMonthAndYear(new Date(event.value.display_start_at as string));
const shouldCheckOverlappingEvents = ref<boolean>(false);
const originalAttendees = ref([...event.value.attendees]);
const templateType = ref([]);

const getTherapyResult = (studentId: number | string) => {
  return event.value.therapy_results.find((result) => result.student_id === studentId);
};

const multiAttendee = ref(
  event.value.attendees.map((a) => {
    a.selectedAttendance = getTherapyResult(a.attendee_id as number)?.id;
    return a;
  }),
);

const hostTimezone = computed<string | undefined>(
  () => event.value?.host_timezone_name || authStore?.user?.timezone?.name,
);

const changeRRuleTimeZone = () => {
  if (eventRRule.value.options && hostTimezone.value) {
    const start = dateUtilities.formatTimeByTimezone(
      event.value?.display_start_at as string,
      hostTimezone.value as string,
      true,
    );

    const until = dateUtilities.formatTimeByTimezone(
      event.value?.display_end_at as string,
      hostTimezone.value as string,
      true,
    );
    eventRRule.value.options.dtstart = new Date(start);

    eventRRule.value.options.until = new Date(until);
  }
};

const displayStart = ref(
  dateUtilities.formatTimeByTimezone(event.value.display_start_at as string, event.value.host_timezone_name as string),
);
const displayEnd = ref(
  dateUtilities.formatTimeByTimezone(event.value.display_end_at as string, event.value.host_timezone_name as string),
);

onMounted(() => init());

const closeDocumentVirtualEventConfirmModal = () => (displayDocumentVirtualEventConfirmModal.value = false);

const init = async () => {
  calendarStore.clearPastDocumentations();
  if (can([PERMISSIONS.THERAPY_RESULT_TYPES_INDEX])) {
    await calendarStore.getTherapyResultTypes();
  }

  if (can([PERMISSIONS.CLINICIAN_STUDENTS_INDEX])) {
    const responseStudents = await ClinicianService.getStudentsOfClinicianRequest(event.value.host_id);
    let availableAttendees = responseStudents.data.data.filter((a) =>
      a.treatment_services.find(
        (s: TreatmentPlanServiceStudentInterface) =>
          s.is_active && s.is_group && (event.value.discipline ? s.discipline.id == event.value.discipline.id : true),
      ),
    );

    const attendeeIdsThatBelongsToEvent = event.value.attendees.map((a) => a.attendee_id);
    availableAttendees = filterAttendeesByOrganization(availableAttendees);
    const attendeeThatDoesNotBelongToEvent = availableAttendees.filter(
      (a: Attendee) => !attendeeIdsThatBelongsToEvent.includes(a.id),
    );

    const is_group = event.value.attendees.find((a) => a.treatment_services.find((s) => s.is_group));

    if (event.value.attendees.length > 1 || is_group) {
      generateFlaggedTherapyResults(attendeeThatDoesNotBelongToEvent);
    }
  }
  prepareBindData();
  changeRRuleTimeZone();
  emit('changeMultiAttendee', multiAttendee.value);
};

const generateFlaggedTherapyResults = (possibleNewAttendees: Attendee[]) => {
  possibleNewAttendees.forEach((attendee) => {
    let cloneAttendee: Attendee = {
      id: originalAttendees.value[0].id,
      attendee_id: attendee.id,
      allowGroup: false,
    };

    cloneAttendee.new = true;
    cloneAttendee.attendee = attendee;

    let goals: GoalMeasurementsInterface[] = [];
    const treatmentServices = attendee.treatment_services.map((treatmentService) => treatmentService);
    treatmentServices.forEach((treatmentService) => goals.push(...treatmentService.goals));
    cloneAttendee.goals = goals;

    const allGoals = goals.map((goal) => {
      return {
        treatment_goal_id: goal.id,
        percentage: 0,
        description: goal.description || '',
        show_goal: false,
        treatment_goal_type_id: goal.treatment_goal_type_id,
        checkbox_order: [],
        total_trials: 10,
        trials: 0,
      };
    });

    const cloneTr = {
      duration: event.value.duration,
      event_id: event.value.id,
      goals_measurements: allGoals,
      notes: undefined,
      student_id: attendee.id,
      therapy_attendee_id: originalAttendees.value[0].id,
      therapy_result_type: undefined,
    };

    event.value.therapy_results.push(cloneTr);
    event.value.attendees.push(cloneAttendee);
  });
};

const prepareBindData = () => {
  event.value.attendees.forEach((attendee: Attendee) => {
    const result = getTherapyResult(attendee.attendee_id as number);

    attendee.lockDocumentGoals = false;

    attendee.goals = attendee.goals?.filter(
      (goal) => !goal.completed_at || findAttendeeGoal(attendee.attendee_id, goal.id)?.id,
    );

    let openGoals = false;
    result?.goals_measurements.forEach((gm) => {
      if (gm.id !== undefined) {
        openGoals = true;
        gm.show_goal = true;
      }

      const goal = attendee.goals?.find((goal) => goal.id === gm.treatment_goal_id);
      if (goal?.completed_at && openGoals) {
        attendee.lockDocumentGoals = true;
      }
    });

    attendee.documentGoals = openGoals;
    attendee.duration = result?.duration ?? event.value.duration;
    attendee.notes = result?.notes ?? undefined;
    attendee.selectedAttendance = result?.therapy_result_type?.id ?? undefined;
  });
};

const shouldShowDuration = (id: number) =>
  (therapyResultTypes.value as TherapyResultTypeInterface[])?.find(
    (attendance) => attendance.id == id && attendance.is_present == true,
  );

const shouldShowDocumentGoalToggle = (attendance_id: number, goalLength: number) =>
  goalLength && therapyResultTypeIsPresent(attendance_id);

const findAttendeeTherapyResults = (id: number) => {
  return event.value.therapy_results.find((result) => {
    return result.student_id === id;
  });
};

/**
 * find the attendee goal measurements by its parent goal.id (treatment_goal_id)
 * @param attendeeId:number
 * @param id:number
 * @returns GoalMeasurementInterface
 */
function findAttendeeGoal(attendeeId: number, id: number) {
  const attendee = findAttendeeTherapyResults(attendeeId);
  return attendee?.goals_measurements?.find((g: GoalMeasurementsInterface) => g.treatment_goal_id === id);
}

const therapyResultTypeIsPresent = (therapyResultTypeId: number) => {
  return (therapyResultTypes.value as TherapyResultTypeInterface[])
    .filter((e: TherapyResultTypeInterface) => e.is_present)
    .find((s: TherapyResultTypeInterface) => s.id == therapyResultTypeId);
};

const isSingleEvent = (): boolean => {
  if (!event.value.rrule) return true;
  const rruleCheck = RRule.fromString(event.value.rrule as string);
  return rruleCheck.count() > 1 ? false : true;
};

const haveNewAttendees = (): boolean => !!payload.value.find((result) => result.new_attendee === true);

const originalsAttendeesWereRemoved = (): boolean => {
  if (payload.value.length < originalAttendees.value.length) {
    return true;
  }

  originalAttendees.value.forEach((attendee: Attendee) => {
    let myAttendeeWasFound = false;
    myAttendeeWasFound =
      !!payload.value.find((result) => result.therapy_attendee_id === attendee.id) || myAttendeeWasFound;
    if (!myAttendeeWasFound) {
      return true;
    }
  });

  return false;
};

const checkGoalsPayload = (attendee: Attendee) => {
  return therapyResultTypeIsPresent(attendee.selectedAttendance as number) &&
    attendee.documentGoals &&
    (attendee.goals?.length as number) > 0
    ? mountGoalsPayload(attendee.attendee_id)
    : [];
};

const mountGoalsPayload = (attendee_id: number) => {
  const result = event.value.therapy_results.find((result) => result.student_id === attendee_id);

  if (!result) {
    return [];
  }

  let goals_measurements: GoalMeasurementsInterface[] = [];

  result.goals_measurements.forEach((gm) => {
    const localGm: GoalMeasurementsInterface = {
      id: gm.id,
      show_goal: gm.show_goal,
      treatment_goal_id: gm.treatment_goal_id,
      total_trials: gm.total_trials,
      trials: gm.trials,
      percentage: gm.percentage,
      checkbox_order: gm.checkbox_order,
    };

    if (gm.show_goal) goals_measurements.push(localGm);
  });

  return goals_measurements;
};

const isDocumenting = computed(
  () => !!payload.value.find((tr) => (tr && tr.notes !== '') || tr.therapy_result_type_id != undefined),
);

const isCaseloadManagement = computed<boolean>(() =>
  calendarStore.isCaseloadManagement(event.value.therapy_event_type),
);

const isEvaluation = computed<boolean>(() => calendarStore.isEvaluation(event.value.therapy_event_type));

const attendeesNotNew = computed(() => event.value.attendees.filter((attendee) => !attendee.new));

/**
 * if the editing event is before today then all types are allowed, if it's not today then
 *  allow no school, clinician cancelled, customer cancelled. if its TODAY then allow a late cancel
 */
const filterTherapyTypeForFutureEvents = computed(() => {
  if ((clickedEventClickArgs.value?.start as Date) < new Date()) return therapyResultTypes.value;

  const allowed = [
    THERAPY_RESULT_TYPES_NAME.NO_SCHOOL,
    THERAPY_RESULT_TYPES_NAME.CLINICIAN_CANCELLED,
    THERAPY_RESULT_TYPES_NAME.CUSTOMER_CANCELLED,
  ];

  const startDate = new Date(clickedEventClickArgs.value?.start as string);
  const today = new Date();
  if (!dateUtilities.sameDay(startDate, today)) {
    return therapyResultTypes.value?.filter((resultType) => {
      if (resultType.name) {
        return allowed.includes(resultType.name as THERAPY_RESULT_TYPES_NAME);
      }
    });
  } else {
    allowed.push(THERAPY_RESULT_TYPES_NAME.LATE_CANCEL);

    return therapyResultTypes.value?.filter((resultType) => {
      if (resultType.name) {
        return allowed.includes(resultType.name as THERAPY_RESULT_TYPES_NAME);
      }
    });
  }
});

const payload = computed(() =>
  multiAttendee.value.map((attendee) => {
    return {
      new_attendee: attendee.new ?? false,
      attendee_id: attendee.attendee_id,
      notes: attendee.notes,
      therapy_result_type_id: attendee.selectedAttendance,
      duration:
        therapyResultTypeIsPresent(attendee.selectedAttendance as number) && attendee.duration > 0
          ? attendee.duration
          : undefined,
      goals_measurements: checkGoalsPayload(attendee),
    };
  }),
);

const rules = computed(() => {
  return {
    therapy_results: {
      $each: helpers.forEach({
        notes: {
          requiredIfCustom: helpers.withMessage(
            'Please select a note',
            requiredIf((v: string, vm) => {
              return vm.therapy_result_type_id !== undefined;
            }),
          ),
        },
        therapy_result_type_id: {
          requiredIfCustom: helpers.withMessage(
            'Please select a note',
            requiredIf((v: string, vm) => {
              return vm && vm.notes !== '';
            }),
          ),
        },
        duration: {
          requiredIfCustom: helpers.withMessage(
            'Please select a duration',
            requiredIf((v: string, vm) => {
              return therapyResultTypeIsPresent(vm.therapy_result_type_id);
            }),
          ),
        },
      }),
    },
  };
});

const canShowEvaluationAssessmentFiles = computed(() => event.value.therapy_event_type === 'Evaluation');

const onSavedClicked = () => {
  submitted.value = true;
  if (v$.value.$invalid) {
    submitted.value = false;
    return;
  }

  if (!isSingleEvent() && (haveNewAttendees() || originalsAttendeesWereRemoved())) {
    displayDocumentVirtualEventConfirmModal.value = true;
  } else {
    onSave();
  }
};

const openPastDocuments = (attendee: Attendee) => {
  displayPastEventsModal.value = !displayPastEventsModal.value;
  emit('openPastDocuments', attendee);
};

const onSave = async () => {
  let newDate = dateUtilities.formatLocalDateTime(event.value.display_start_at as string, true, true);
  newDate = dateUtilities.formatDateForApi(newDate, true);

  closeDocumentVirtualEventConfirmModal();
  submitted.value = true;
  if (v$.value.$invalid) {
    submitted.value = false;
    return;
  }

  const updated = eventsModalsStore.selectedEventCompletedToEdit ? true : undefined;

  const response = await calendarStore.storeUpdateTherapyResults(
    {
      event_id: event.value.id,
      event_date: newDate,
      start_at: useDateTime().formatDateForApiWithoutConvertTimezone(eventRRule.value.options?.dtstart, true),
      until: useDateTime().formatDateForApiWithoutConvertTimezone(eventRRule.value.options?.until, true),
      therapy_results: payload.value as TherapyResultsInterface[],
      updated,
      duration: event.value.duration,
    },
    originalAttendees.value,
  );

  let messages = '';

  if (response.data.errors !== undefined) {
    let errors = response.data.errors;
    let keys = Object.keys(errors);
    keys.forEach((key) => {
      errors[key].forEach((error: string) => {
        messages += `\n${error}`;
      });
    });
  }

  emit(EMIT.SAVE_DOCUMENT_EVENT, response.status, messages);
};

const v$ = useVuelidate(rules, { therapy_results: payload });

/**
 * update the goal from the TrailGoals->GoalsCheckbox
 * @params {checkboxArray: number[],
 *   max: number,
 *   attendeeId: number,
 *   trialId: number,
 *   trialCount: number,
 *   percentage: number,
 *   type: string}
 */
const updateTrialGoal = (
  value: number[],
  max: number,
  attendeeId: number,
  treatment_goal_id: number,
  trialCount: number,
  percentage: number,
) => {
  const goal: GoalMeasurementsInterface = findAttendeeGoal(attendeeId, treatment_goal_id) as GoalMeasurementsInterface;
  if (goal) {
    goal.checkbox_order = value;
    goal.total_trials = max;
    goal.percentage = percentage;
    goal.trials = trialCount;
  }
};

/**
 * update the slider goal
 * * @params {
 *   attendeeId: number,
 *   goalId: number,
 *   percentage: number,
 *   type: string}
 */
const updatePercentageGoal = (attendeeId: number, treatment_goal_id: number, percentage: number) => {
  const goal: GoalMeasurementsInterface = findAttendeeGoal(attendeeId, treatment_goal_id) as GoalMeasurementsInterface;
  if (goal) goal.percentage = percentage;
};

const updateShouldSaveGoals = (saveGoals: boolean, goalId: number, attendeeId: number) => {
  const goal: GoalMeasurementsInterface = findAttendeeGoal(attendeeId, goalId) as GoalMeasurementsInterface;
  if (goal) goal.show_goal = saveGoals;
};

/**
 * --find the goal measurements for the attendee and attach whether to save/show the goal for the toggle
 * --also declare what component is needed for the goalType (trial or percentage)
 * @param attendee_id {number}
 * @param goals {GoalInterface[]}
 * @return measurements {GoalMeasurementsInterface[]}
 */
const attendeeGoalMeasurements = (attendee_id: number, goals: GoalInterface[]) => {
  const measurements: GoalMeasurementsInterface[] = [];
  const attendeeTherapyResults = event.value.therapy_results.find((result) => result.student_id === attendee_id);

  goals.forEach((goal) => {
    const goalType = attendeeTherapyResults?.goals_measurements?.find((result) => goal.id === result.treatment_goal_id);

    if (goalType) {
      goalType.completed_at = goal.completed_at;
      goalType.show_goal = goalType.show_goal || false;
      goalType.description = goal.description;
      goalType.component =
        goal.treatment_goal_type.title === GOAL_TYPE.TRIALS ? markRaw(TrialsGoals) : markRaw(PercentageGoals);
      measurements.push(goalType);
    }
  });
  return measurements;
};

const emitEditEvent = () => emit(EMIT.EDIT_EVENT);

const rruleWeekdayNumberToString = (weekday: number) => {
  const maps = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return maps[weekday];
};

const recurrenceToDisplay = () => {
  if (!event.value.rrule) {
    return '';
  }

  if (event.value.rrule.includes('DAILY')) {
    return 'Repeats Daily';
  }

  const rrule = RRule.fromString(event.value.rrule);

  if (event.value.rrule.includes('WEEKLY')) {
    const weekdays = rrule.options.byweekday.map(rruleWeekdayNumberToString);
    const lastElement = weekdays.pop();
    return 'Repeats Weekly on ' + weekdays.join(', ') + (weekdays.length ? ' and ' : '') + lastElement;
  }

  if (event.value.rrule.includes('YEARLY')) {
    return 'Repeats Annually on ' + rrule?.options?.dtstart.toLocaleString('default', { weekday: 'long' });
  }

  if (event.value.rrule.includes('MONTHLY')) {
    const weekNumbers = ['1st', '2nd', '3rd', '4th', '5th'];
    const weekNumber = Math.floor(rrule?.options?.dtstart?.getDate() / 7);
    return `Repeats Monthly on the ${weekNumbers[weekNumber]} ${rrule?.options?.dtstart.toLocaleString('default', {
      weekday: 'long',
    })}`;
  }

  return '';
};

const setTemplateOnNotes = async (index: number) => {
  if (templateType.value[index] === 'Progress Notes') {
    multiAttendee.value[index].notes = templates.progressNotesTemplate;
  } else {
    multiAttendee.value[index].notes = templates.soapTemplate;
  }
  await nextTick();
  let item = document.getElementsByClassName('ql-editor').item(index)?.firstChild as Node;
  document.getSelection()?.setPosition(item, 1);
};

//provide the updateGoals functions for the grand-child
provide('updateTrialGoal', updateTrialGoal);
provide('updatePercentageGoal', updatePercentageGoal);

const evaluteToCanGroup = computed(() => {
  const { attendees } = event.value;
  const is_group = event.value.attendees.find(
    (a) =>
      !a.treatment_services?.find((s) => {
        return s.is_group;
      }),
  );

  if (attendees.length === 1 && !is_group) {
    return true;
  }

  return false;
});

const eventDescription = computed(() => {
  const { event_type_data, therapy_results } = event.value;
  return !isCaseloadManagement.value && event_type_data ? event_type_data.description : therapy_results[0].notes;
});

const filterAttendeesByOrganization = (availableAttendees: Attendee[]) => {
  const allowedOrg = event.value.attendees[0]?.organizations[0].id;
  return availableAttendees.filter((attendee) => {
    if (attendee.organizations.length) return attendee.organizations[0].id === allowedOrg;
  });
};

const onAttendeeChange = () => {
  emit('changeMultiAttendee', multiAttendee.value);
};

const handleOverlappingError = () => {
  if (!usePersonTypes.isSupportAdmin() && !usePersonTypes.isSuperAdmin()) {
    overlappingError.value = true;
  }
};

const { initialRRule } = useGetInitialRRuleObject(event.value as DetailedEventInterface, true);

const eventRRule = ref<Partial<RRule>>(initialRRule.value);

const handleOverlappingOk = () => {
  shouldCheckOverlappingEvents.value = false;
  overlappingError.value = false;
};

const isValidCaseloadManagement = computed(
  () =>
    event.value?.attendees[0]?.organizations.length &&
    event.value.therapy_event_type === EVENT_TYPE.CASELOAD_MANAGEMENT,
);

const checkOverlappingEvents = () => {
  shouldCheckOverlappingEvents.value = true;
};

//This modification to the Editor component is required for compliance with Quill v2
//eslint-disable-next-line
(Editor as any).methods.renderValue = function renderValue(this: { quill?: Quill }, value: string) {
  if (this.quill) {
    if (value) {
      const delta = this.quill.clipboard.convert({ html: value });
      this.quill.setContents(delta, 'silent');
    } else {
      this.quill.setText('');
    }
  }
};
</script>

<template>
  <div class="document-event-modal-content py-4 px-3 lg:px-4">
    <div class="document-event-description w-full">
      <CalendarOverlappingEvents
        v-if="shouldCheckOverlappingEvents"
        :duration="event.duration"
        :event-id="event.id"
        :is-recurring="false"
        :rrule="eventRRule"
        @error="handleOverlappingError"
        @ok="handleOverlappingOk"
      />
      <div class="mb-3 flex align-items-center">
        <Icon name="event" class="date-picker-icon mr-3" />
        <div>
          <p>
            <b class="mr-1">{{ dateUtilities.formatLongDate(event.event_date) }}</b>

            {{ displayStart }}&mdash;
            {{ displayEnd }}
            {{ event.host_timezone_abbr }}
          </p>
          <p v-if="isAdmin || isLocked" class="font-bold mt-1 text-dark-medium">
            {{ recurrenceToDisplay() }}
          </p>
        </div>
      </div>
      <p v-if="isValidCaseloadManagement" class="mb-3 flex align-items-center">
        <Icon name="domain" class="material-symbols-outlined mr-3 text-color-blue" />
        <b class="mr-1">{{ event.attendees[0].organizations[0].name }}</b>
      </p>
      <p v-if="eventDescription" class="mb-3 flex align-items-center">
        <Icon name="notes" class="description-icon material-symbols-outlined mr-3 text-color-orange" />
        {{ eventDescription }}
      </p>
      <p
        v-if="isClinician || (isCaseloadManagement && !isLocked)"
        class="mb-3 attendees-select flex align-items-center sm:w-11"
      >
        <Icon v-if="multiAttendee.length <= 1" name="person" class="text-color-blue material-symbols-outlined mr-3" />
        <Icon v-else name="group" class="text-color-blue material-symbols-outlined mr-3" />
        <MultiSelect
          v-if="!evaluteToCanGroup && !isCaseloadManagement && !isLocked"
          id="attendees"
          v-model="multiAttendee"
          class="multiselect-custom w-full"
          panel-class="new-event-attendee-select"
          :options="event.attendees"
          option-label="attendee.full_name"
          :filter="true"
          display="chip"
          placeholder="Select Attendees"
          :show-toggle-all="false"
          @change="onAttendeeChange"
        />
        <span v-else class="flex justify-content-between font-bold">
          {{ multiAttendee.map((item) => item.attendee?.display_name).join(', ') }}
        </span>
      </p>
      <div v-if="isCaseloadManagement" class="pt-3 mt-4 mb-3 border-top-3 border-gray-200">
        <label>Caseload Management Type(s)</label>
        <div class="mt-2">
          <Chip
            v-for="item in event.event_type_data.therapy_caseload_management_types"
            :key="item.id"
            :label="item.name"
            class="px-2 mr-2 border-round-sm bg-gray-200 text-sm font-bold"
          />
        </div>
      </div>
      <div v-if="canShowEvaluationAssessmentFiles" class="mb-3 attendees-select flex align-items-start sm:w-11">
        <Icon name="attachment" class="material-symbols-outlined mr-3 text-color-purple" />
        <EventEvaluationAssessmentsFiles :event="event" show-assessment-title />
      </div>
      <div v-if="isEvaluation && event" class="pt-3 pl-1 mt-4 mb-3 border-top-3 border-gray-200">
        <h3 class="font-bold">Evaluation Type:</h3>
        <span class="my-2" v-text="event.evaluation?.evaluation_type.title" />
      </div>
    </div>
    <ul
      v-if="event.attendees && isClinician && !isCaseloadManagement && !isLocked"
      class="document-event-attendees flex flex-column p-0 m-0 list-none"
    >
      <li v-for="(attendee, index) in multiAttendee" :key="attendee.id" class="mb-3 p-3 bg-white">
        <h3 v-if="attendee.attendee" class="flex justify-content-between">
          <b>{{ attendee.attendee.full_name }}</b>
          <Icon
            name="history"
            class="text-color-gray cursor-pointer p-2"
            @click="openPastDocuments(attendee.attendee)"
          />
        </h3>
        <div class="field-row flex flex-column">
          <div class="field mt-3 required flex flex-column">
            <label>Attendance</label>
            <Dropdown
              v-model="attendee.selectedAttendance"
              option-label="name"
              option-value="id"
              :options="filterTherapyTypeForFutureEvents"
              placeholder="Select attendance"
              class="w-20rem"
              @change="checkOverlappingEvents"
            />
            <div v-if="submitted">
              <div
                v-for="error in v$.therapy_results.$each.$response.$errors[index].therapy_result_type_id"
                :key="error"
                class="input-errors"
              >
                <div class="p-error">{{ error.$message }}</div>
              </div>
            </div>
          </div>
          <div class="field flex flex-1 flex-column mt-3 required">
            <div
              class="flex w-full justify-content-end mt-2 h-2rem -ml-2 w-9rem ml-auto mr-3"
              style="margin-bottom: -60px"
            >
              <Dropdown
                :key="attendee.id"
                v-model="templateType[index]"
                :options="['Progress Notes', 'SOAP']"
                placeholder="Add a template"
                class="flex align-items-center"
                style="background-color: transparent; border: none"
                @change="setTemplateOnNotes(index)"
              />
            </div>
            <div class="field flex flex-column required h-10rem">
              <label>Notes</label>
              <Editor v-model="attendee.notes" class="h-8rem">
                <template #toolbar>
                  <div class="flex">
                    <div class="mr-2">
                      <button class="ql-bold"></button>
                      <button class="ql-italic"></button>
                      <button class="ql-underline"></button>
                    </div>
                    <div>
                      <button class="ql-list" value="ordered"></button>
                      <button class="ql-list" value="bullet"></button>
                      <select class="ql-align"></select>
                    </div>
                  </div>
                </template>
              </Editor>
            </div>
            <div v-if="submitted">
              <div
                v-for="error in v$.therapy_results.$each.$response.$errors[index].notes"
                :key="error"
                class="input-errors"
              >
                <div class="p-error">{{ error.$message }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="shouldShowDuration(attendee.selectedAttendance)" class="field-row flex">
          <div class="field flex flex-column mt-3">
            <label class="required">Duration</label>
            <div class="flex-row">
              <div class="flex align-items-center">
                <InputNumber
                  v-model="attendee.duration"
                  :step="15"
                  class="w-4"
                  input-class="w-2"
                  :use-grouping="false"
                  :allow-empty="false"
                />
                <label class="document-event-attendees__minutes font-bold ml-2">Minutes</label>
              </div>
              <div
                v-for="error in v$.therapy_results.$each.$response.$errors[index].duration"
                :key="error"
                class="input-errors"
              >
                <div class="p-error">{{ error.$message }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="document-event-goals">
          <div
            v-if="shouldShowDocumentGoalToggle(attendee.selectedAttendance, attendee.goals.length)"
            class="document-event-goals-switch flex mt-3 pt-3 align-items-center"
          >
            <InputSwitch v-model="attendee.documentGoals" :disabled="attendee.lockDocumentGoals" />
            <p class="ml-2">Document Goals</p>
          </div>
          <div v-if="attendee.documentGoals" class="document-event-goals">
            <div v-for="value in attendeeGoalMeasurements(attendee.attendee_id, attendee.goals)" :key="value.id">
              <component
                :is="value.component"
                :goal="value"
                :attendee-id="attendee.attendee_id"
                :display-past-events-modal="documentModalOpened"
                @should-save-goals="updateShouldSaveGoals"
              />
            </div>
          </div>
        </div>
      </li>
    </ul>
    <div v-if="isAdmin || (isLocked && !isCaseloadManagement)">
      <DocumentationItem
        v-for="attendee in attendeesNotNew"
        :key="attendee.id"
        :attendee="attendee"
        :therapy-result="getTherapyResult(attendee.attendee_id)"
        :event="event"
      />
    </div>
  </div>
  <div v-if="isClinician && !isLocked" class="p-dialog-footer document-event-modal-footer flex justify-content-between">
    <AppButton
      type="secondary"
      text="Edit Event"
      icon-left="edit"
      :disabled="!isEditable && event.completed"
      @click="emitEditEvent"
    />
    <div class="flex flex-row">
      <AppButton type="secondary" text="Cancel" @click="emit(EMIT.CLOSE_MODAL)" />
      <AppButton
        type="primary"
        text="Save"
        :disabled="v$.$invalid || !isDocumenting || overlappingError"
        @click="onSavedClicked"
      />
    </div>
  </div>
  <Dialog
    v-model:visible="displayDocumentVirtualEventConfirmModal"
    :modal="true"
    :draggable="false"
    class="document-event-modal"
  >
    <template #header>
      <h2>Edit recurring event</h2>
    </template>
    <EventProcessTypeModal @close-confirm-modal="closeDocumentVirtualEventConfirmModal" @document-event="onSave" />
  </Dialog>
</template>

<style scoped lang="scss">
.document-event-modal-content {
  background: $grayLight;
  max-width: 720px;
  min-width: 540px;

  .field {
    .p-inputwrapper {
      width: 100%;
    }
  }

  .document-event-goals-switch {
    border-top: 0.125rem solid $grayLight;
  }

  ul.document-event-attendees {
    li {
      border-radius: 0.5rem;

      .p-inputwrapper {
        border-color: $grayLight;
      }

      .document-event-attendees__minutes {
        color: $grayDark;
      }
    }
  }
}

::v-deep(.p-inputwrapper .p-multiselect-label-container .p-multiselect-label) {
  .p-multiselect-token {
    background: #bfebfb;
    color: #26728e;
    font-weight: bold;
  }

  .p-multiselect-token-icon {
    color: #40b3dd;
  }

  .pi-times-circle:before {
    content: '\e90b';
    font-size: 12px;
  }
}

::v-deep(.p-dropdown:first-child > .p-dropdown-label) {
  color: #078888;
  font-size: 0.875rem;
}

::v-deep(.p-dropdown:first-child > .p-dropdown-trigger) {
  color: #078888;
  font-size: 0.875rem;
  max-width: min-content;
}

.attendees-select .group-icon {
  color: #40b3dd;
}

.date-picker-icon {
  color: var(--primary-color-medium);
}

.goal-card {
  background: $grayLight;
  border-radius: 0.25rem;
}

.dark-medium {
  color: $grayDark;
}

.text-dark-medium {
  color: $darkMedium;
}
</style>
